"use client";

import { useEffect, useRef, useState } from "react";
import { Bot, MessageCircle, Sparkles, Send, X, ChevronRight, Star, MapPin, Users, Wifi, Car, Utensils, Waves, Clock, Calendar, Map, Compass, Sun, Moon, Mic, MicOff } from "lucide-react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import HotelCard from "./HotelCard";
import RestaurantCard from "./restaurant-card";

interface AnimatedChatbotProps {
  size?: "sm" | "md" | "lg";
  hasNotification?: boolean;
  className?: string;
}

interface Hotel {
  _id: string;
  name: string;
  name_en: string;
  address: string;
  address_en: string;
  description: string;
  description_en: string;
  cover: string;
  images: string[];
  note_google: string;
  avis_google?: string;
  category_code_description: string;
  starting_price: number;
  ville: string;
  ville_en: string;
  lat: string;
  lng: string;
  settings?: {
    is_air_conditioner_included?: boolean;
  };
}

interface Restaurant {
  _id: string;
  name: string;
  name_en: string;
  address: string;
  address_en: string;
  description: string;
  description_en: string;
  images: string[];
  note_google: string;
  avis_google?: string;
  category_code_description: string;
  ville: string;
  ville_en: string;
  lat: string;
  lng: string;
  opening_hours?: {
    [key: string]: string;
  };
}

interface Circuit {
  id: number;
  name: string;
  duration: string;
  regions: string;
  highlights: string[];
  effortLevel: string;
  accommodation: string;
  icon?: React.ReactNode;
}

interface ChatResponse {
  response: string;
  data?: {
    hotels?: Hotel[];
    restaurants?: Restaurant[];
    circuits?: Circuit[];
  };
  context?: {
    last_intent?: string;
    last_location?: string;
    has_markdown_table?: boolean;
  };
  conversation_title: string;
  is_guest: boolean;
  session_id: string;
  user_id: string;
  user_language: string;
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  data?: {
    hotels?: Hotel[];
    restaurants?: Restaurant[];
    circuits?: Circuit[];
  };
  context?: {
    last_intent?: string;
    has_markdown_table?: boolean;
  };
}

const AnimatedChatbot: React.FC<AnimatedChatbotProps> = ({
  size = "md",
  hasNotification = true,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [eyePosition, setEyePosition] = useState({ x: 0, y: 0 });
  const [showNotification, setShowNotification] = useState(hasNotification);
  const [showSparkles, setShowSparkles] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  
  // Voice states
  const [isListening, setIsListening] = useState(false);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(true);
  const [speechRecognition, setSpeechRecognition] = useState<any>(null);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en-US');
  const [voiceInputBuffer, setVoiceInputBuffer] = useState<string>("");
  const [isAutoSendEnabled, setIsAutoSendEnabled] = useState(true);

  // Size configuration with responsive design
  const sizeClasses = {
    sm: {
      container: "w-12 h-12 sm:w-14 sm:h-14",
      icon: "w-3 h-3 sm:w-4 sm:h-4",
      eye: "w-1 h-1 sm:w-1.5 sm:h-1.5",
      notification: "w-2 h-2 top-[-2px] right-[-2px]",
      borderRadius: "rounded-lg",
      tooltip: "text-xs px-2 py-1",
    },
    md: {
      container: "w-14 h-14 sm:w-16 sm:h-16",
      icon: "w-4 h-4 sm:w-5 sm:h-5",
      eye: "w-1.5 h-1.5 sm:w-2 sm:h-2",
      notification: "w-2.5 h-2.5 top-[-3px] right-[-3px]",
      borderRadius: "rounded-xl",
      tooltip: "text-sm px-3 py-1.5",
    },
    lg: {
      container: "w-16 h-16 sm:w-18 sm:h-18",
      icon: "w-5 h-5 sm:w-6 sm:h-6",
      eye: "w-2 h-2 sm:w-2.5 sm:h-2.5",
      notification: "w-3 h-3 top-[-4px] right-[-4px]",
      borderRadius: "rounded-2xl",
      tooltip: "text-base px-4 py-2",
    },
  };

  const currentSize = sizeClasses[size];

  // Circuit icons mapping
  const circuitIcons: Record<string, React.ReactNode> = {
    "Mediterranean & Heritage": <Compass className="w-5 h-5 text-blue-600" />,
    "Sahara & Oasis": <Sun className="w-5 h-5 text-orange-500" />,
    "Seaside East Coast": <Waves className="w-5 h-5 text-teal-500" />,
    "Djerba Discovery": <Map className="w-5 h-5 text-purple-500" />,
    "Adventure & Water Sports": <Waves className="w-5 h-5 text-green-500" />,
  };

  // Parse markdown table into structured data
  const parseMarkdownTable = (markdown: string): Circuit[] => {
    const circuits: Circuit[] = [];
    
    try {
      // Split by rows and find the table
      const lines = markdown.split('\n');
      let inTable = false;
      let headers: string[] = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        if (line.startsWith('|')) {
          if (!inTable) {
            inTable = true;
            // Parse headers (first row after table start)
            const nextLine = lines[i + 1];
            if (nextLine && nextLine.includes('|---')) {
              headers = line.split('|')
                .filter(cell => cell.trim() !== '')
                .map(cell => cell.trim());
              i += 2; // Skip header and separator lines
            }
          } else {
            // Parse data rows
            const cells = line.split('|')
              .filter(cell => cell.trim() !== '')
              .map(cell => cell.trim());
            
            if (cells.length >= 6) {
              const circuitName = cells[1].replace(/\*\*/g, '').trim();
              const duration = cells[2].trim();
              const regions = cells[3].trim();
              const highlights = cells[4].split('<br>').map(h => h.replace(/^•\s*/, '').trim()).filter(h => h);
              const effortLevel = cells[5].trim();
              const accommodation = cells[6]?.trim() || '';
              
              circuits.push({
                id: parseInt(cells[0].replace(/\*\*/g, '')) || circuits.length + 1,
                name: circuitName,
                duration,
                regions,
                highlights,
                effortLevel,
                accommodation,
                icon: circuitIcons[circuitName] || <Compass className="w-5 h-5 text-gray-600" />
              });
            }
          }
        } else if (inTable && line === '') {
          inTable = false;
        }
      }
    } catch (error) {
      console.error("Error parsing markdown table:", error);
    }
    
    return circuits;
  };

  // Function to extract markdown table and clean text
  const processMarkdownResponse = (text: string): { 
    cleanText: string; 
    circuits: Circuit[];
    hasTable: boolean;
  } => {
    const circuits = parseMarkdownTable(text);
    const hasTable = circuits.length > 0;
    
    // Clean text by removing the table section for regular display
    const tableStart = text.indexOf('| No |');
    const tableEnd = text.indexOf('\n\n', tableStart + 50); // Look for double newline after table
    let cleanText = text;
    
    if (tableStart !== -1 && tableEnd !== -1) {
      cleanText = text.substring(0, tableStart).trim();
    }
    
    return { cleanText, circuits, hasTable };
  };

  // Initialize speech recognition
  useEffect(() => {
    const initSpeechRecognition = () => {
      // Check for browser support
      const SpeechRecognition = (window as any).SpeechRecognition || 
                              (window as any).webkitSpeechRecognition;
      
      if (!SpeechRecognition) {
        console.warn('Speech recognition not supported in this browser');
        setIsVoiceEnabled(false);
        return;
      }

      const recognition = new SpeechRecognition();
      
      // Configure recognition
      recognition.continuous = true; // Keep listening
      recognition.interimResults = true; // Get results as you speak
      recognition.lang = selectedLanguage;
      recognition.maxAlternatives = 1;
      
      // Event handlers
      recognition.onstart = () => {
        console.log('Speech recognition started');
        setIsListening(true);
        setInterimTranscript("");
        setVoiceInputBuffer("");
      };
      
      recognition.onend = () => {
        console.log('Speech recognition ended');
        setIsListening(false);
        setInterimTranscript("");
        
        // Auto-send message when voice input ends
        if (isAutoSendEnabled && voiceInputBuffer.trim() && !isLoading) {
          setTimeout(() => {
            sendVoiceMessage(voiceInputBuffer.trim());
          }, 300);
        }
      };
      
      recognition.onresult = (event: any) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          const transcript = event.results[i][0].transcript;
          
          if (event.results[i].isFinal) {
            // Final result
            finalTranscript += transcript;
          } else {
            // Interim result
            interimTranscript += transcript;
          }
        }
        
        // Update input message with final results
        if (finalTranscript) {
          const newText = voiceInputBuffer + finalTranscript;
          setVoiceInputBuffer(newText);
          setInputMessage(newText);
        }
        
        // Show interim results
        if (interimTranscript) {
          setInterimTranscript(interimTranscript);
        }
      };
      
      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        setInterimTranscript("");
        
        // Don't restart automatically for these errors
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          setIsVoiceEnabled(false);
          alert('Microphone access denied. Please enable microphone permissions.');
        }
      };
      
      setSpeechRecognition(recognition);
    };

    initSpeechRecognition();

    return () => {
      if (speechRecognition) {
        speechRecognition.stop();
      }
    };
  }, [selectedLanguage, isAutoSendEnabled]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Eye follow mouse
    const handleMouseMove = (e: MouseEvent) => {
      const containerRect = container.getBoundingClientRect();
      const centerX = containerRect.left + containerRect.width / 2;
      const centerY = containerRect.top + containerRect.height / 2;

      const dx = e.clientX - centerX;
      const dy = e.clientY - centerY;

      const maxDistance = 15;
      const distance = Math.min(Math.sqrt(dx * dx + dy * dy), maxDistance);
      const angle = Math.atan2(dy, dx);

      const offsetX = Math.cos(angle) * distance * 0.04;
      const offsetY = Math.sin(angle) * distance * 0.03;

      setEyePosition({ x: offsetX, y: offsetY });
    };

    // Random sparkle effects
    const sparkleInterval = setInterval(() => {
      if (!isHovered && Math.random() > 0.8) {
        setShowSparkles(true);
        setTimeout(() => setShowSparkles(false), 1000);
      }
    }, 4000);

    // Random notification pulse
    const notificationInterval = setInterval(() => {
      if (showNotification && !isChatOpen) {
        setShowNotification(false);
        setTimeout(() => setShowNotification(true), 1000);
      }
    }, 5000);

    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      clearInterval(sparkleInterval);
      clearInterval(notificationInterval);
    };
  }, [isHovered, showNotification, isChatOpen]);

  useEffect(() => {
    // Initialize with welcome message
    if (messages.length === 0 && isChatOpen) {
      const welcomeMessage: Message = {
        id: 'welcome',
        text: "Hello! I'm your Tunisia travel assistant. How can I help you today? You can ask about hotels, restaurants, tourist circuits, destinations, or travel tips in Tunisia.",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, [isChatOpen]);

  useEffect(() => {
    // Auto scroll to bottom when new messages arrive
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleClick = () => {
    setIsActive(true);
    setShowNotification(false);
    setIsChatOpen(!isChatOpen);
    setTimeout(() => setIsActive(false), 2000);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
  };

  // Toggle voice listening
  const toggleVoiceInput = () => {
    if (!speechRecognition || !isVoiceEnabled) return;
    
    if (isListening) {
      // Stop listening - will trigger auto-send
      speechRecognition.stop();
    } else {
      try {
        speechRecognition.start();
      } catch (error) {
        console.error('Failed to start speech recognition:', error);
        setIsVoiceEnabled(false);
      }
    }
  };

  // Handle language change for speech recognition
  const handleLanguageChange = (lang: string) => {
    // Map language codes
    const langMap: Record<string, string> = {
      'en': 'en-US',
      'fr': 'fr-FR',
      'ar': 'ar-SA',
      'es': 'es-ES',
      'de': 'de-DE',
    };
    
    const speechLang = langMap[lang] || 'en-US';
    setSelectedLanguage(speechLang);
    
    // If currently listening, stop and restart with new language
    if (isListening && speechRecognition) {
      speechRecognition.stop();
      setTimeout(() => {
        if (speechRecognition) {
          speechRecognition.lang = speechLang;
          speechRecognition.start();
        }
      }, 300);
    }
  };

  // Send message from voice input
  const sendVoiceMessage = async (voiceText: string) => {
    if (!voiceText.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: voiceText,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setVoiceInputBuffer("");
    setIsLoading(true);

    try {
      const response = await axios.post<ChatResponse>(
        "/api/chat",
        {
          message: voiceText,
          user_id: "aaaa",
          session_id: "bbbb",
        }
      );

      const data = response.data;

      if (!userId) setUserId(data.user_id);
      if (!sessionId) setSessionId(data.session_id);

      // Process the response to extract markdown tables
      const { cleanText, circuits, hasTable } = processMarkdownResponse(data.response);

      const botMessage: Message = {
        id: Date.now().toString() + "-bot",
        text: cleanText || data.response,
        sender: "bot",
        timestamp: new Date(),
        data: {
          ...data.data,
          circuits: circuits.length > 0 ? circuits : undefined
        },
        context: {
          ...data.context,
          has_markdown_table: hasTable
        },
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error("Error sending message:", error);

      setMessages(prev => [
        ...prev,
        {
          id: Date.now().toString() + "-error",
          text: "Sorry, I encountered an error. Please try again.",
          sender: "bot",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Send message from manual input
  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputMessage,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setVoiceInputBuffer("");
    setIsLoading(true);

    try {
      const response = await axios.post<ChatResponse>(
        "/api/chat",
        {
          message: inputMessage,
          user_id: "aaaa",
          session_id: "bbbb",
        }
      );

      const data = response.data;

      if (!userId) setUserId(data.user_id);
      if (!sessionId) setSessionId(data.session_id);

      // Process the response to extract markdown tables
      const { cleanText, circuits, hasTable } = processMarkdownResponse(data.response);

      const botMessage: Message = {
        id: Date.now().toString() + "-bot",
        text: cleanText || data.response,
        sender: "bot",
        timestamp: new Date(),
        data: {
          ...data.data,
          circuits: circuits.length > 0 ? circuits : undefined
        },
        context: {
          ...data.context,
          has_markdown_table: hasTable
        },
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error("Error sending message:", error);

      setMessages(prev => [
        ...prev,
        {
          id: Date.now().toString() + "-error",
          text: "Sorry, I encountered an error. Please try again.",
          sender: "bot",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    } else if (e.key === ' ' && e.ctrlKey && isVoiceEnabled) {
      // Ctrl+Space for voice input
      e.preventDefault();
      toggleVoiceInput();
    }
  };

  // Helper function to transform API hotel data to HotelCard format
  const transformHotelData = (hotel: any) => {
    return {
      vignette: hotel.cover || hotel.images?.[0] || "https://cdn.tunisiagotravel.com/unsafe/600x400/smart/filters:format(webp)/hotels/default.jpg",
      name: hotel.name_en || hotel.name,
      address: hotel.address_en || hotel.address || `${hotel.ville_en || hotel.ville}, Tunisia`,
      ...hotel
    };
  };

  // Helper function to transform API restaurant data to RestaurantCard format
  const transformRestaurantData = (restaurant: any) => {
    return {
      name: restaurant.name_en || restaurant.name,
      address: restaurant.address_en || restaurant.address || `${restaurant.ville_en || restaurant.ville}, Tunisia`,
      images: restaurant.images || [],
      opening_hours: restaurant.opening_hours || {},
      ...restaurant
    };
  };

  // Render a circuit card
  const renderCircuitCard = (circuit: Circuit) => {
    return (
      <div key={circuit.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
        <div className="p-3 sm:p-4">
          {/* Header with icon and title */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2 sm:gap-3 flex-1">
              <div className="p-1.5 sm:p-2 bg-gray-50 rounded-lg flex-shrink-0">
                {circuit.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm sm:text-base text-gray-800 truncate">{circuit.name}</h4>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1">
                  <span className="flex items-center gap-1 text-xs sm:text-sm text-gray-600">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                    {circuit.duration}
                  </span>
                  <span className="hidden sm:inline text-sm text-gray-600">•</span>
                  <span className="text-xs sm:text-sm text-gray-600">
                    Effort: {circuit.effortLevel}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-primary/10 text-primary px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium flex-shrink-0">
              #{circuit.id}
            </div>
          </div>

          {/* Regions */}
          <div className="mb-3 sm:mb-4">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
              <span className="text-xs sm:text-sm font-medium text-gray-700">Regions:</span>
            </div>
            <p className="text-gray-600 text-xs sm:text-sm pl-5 sm:pl-6">{circuit.regions}</p>
          </div>

          {/* Highlights */}
          <div className="mb-3 sm:mb-4">
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500" />
              <span className="text-xs sm:text-sm font-medium text-gray-700">Highlights:</span>
            </div>
            <ul className="space-y-1 pl-5 sm:pl-6">
              {circuit.highlights.slice(0, 3).map((highlight, index) => (
                <li key={index} className="text-gray-600 text-xs sm:text-sm flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span className="flex-1">{highlight}</span>
                </li>
              ))}
              {circuit.highlights.length > 3 && (
                <li className="text-gray-500 text-xs sm:text-sm">
                  + {circuit.highlights.length - 3} more highlights
                </li>
              )}
            </ul>
          </div>

          {/* Accommodation */}
          <div className="pt-3 border-t border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                <span className="text-xs sm:text-sm text-gray-600">Accommodation:</span>
              </div>
              <span className="text-xs sm:text-sm font-medium text-gray-800 sm:text-right">{circuit.accommodation}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Voice input status component
  const VoiceStatusIndicator = () => {
    if (!isVoiceEnabled) {
      return (
        <div className="flex items-center gap-1 text-xs text-red-500">
          <MicOff className="w-3 h-3" />
          <span>Voice disabled</span>
        </div>
      );
    }

    if (isListening) {
      return (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>
          <span className="text-xs text-red-600">Listening... (Auto-send: ON)</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1 text-xs text-green-600">
        <Mic className="w-3 h-3" />
        <span>Voice ready (Auto-send: ON)</span>
      </div>
    );
  };

  // Toggle auto-send feature
  const toggleAutoSend = () => {
    setIsAutoSendEnabled(!isAutoSendEnabled);
  };

  // Render input area with voice controls
  const renderInputArea = () => {
    return (
      <div className="p-3 sm:p-4 border-t border-gray-200">
        {/* Voice status bar */}
          {/* Settings */}
          <div className="flex flex-col sm:flex-row justify-between gap-2 mb-2">
            <button
              onClick={toggleAutoSend}
              className={`text-xs px-3 py-2 sm:px-2 sm:py-1 rounded-lg transition-colors min-h-[44px] sm:min-h-0 ${
                isAutoSendEnabled 
                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title={isAutoSendEnabled ? "Disable auto-send" : "Enable auto-send"}
            >
              {isAutoSendEnabled ? 'Auto-send: ON' : 'Auto-send: OFF'}
            </button>
            
            {/* Voice language selector */}
            <select
              defaultValue="en"
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="text-xs border border-gray-300 rounded-lg px-3 py-2 sm:px-2 sm:py-1 bg-white min-h-[44px] sm:min-h-0"
              disabled={isListening}
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="ar">العربية</option>
              <option value="es">Español</option>
              <option value="de">Deutsch</option>
            </select>
        </div>
        
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={isListening ? "Speaking..." : "Type your message"}
              className="w-full px-4 py-3 sm:py-3 bg-gray-100 rounded-xl text-black text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:bg-white pr-14 min-h-[44px]"
              disabled={isLoading || isListening}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {/* Voice input button */}
              <button
                onClick={toggleVoiceInput}
                className={`p-2 rounded-full transition-all min-w-[40px] min-h-[40px] flex items-center justify-center ${
                  isListening 
                    ? 'bg-red-100 text-red-600 animate-pulse ring-2 ring-red-300' 
                    : isVoiceEnabled
                      ? 'bg-primary/10 text-primary hover:bg-primary/20'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                disabled={!isVoiceEnabled || isLoading}
                title={isListening ? "Stop listening (will auto-send)" : "Start voice input"}
              >
                {isListening ? (
                  <div className="relative">
                    <Mic className="w-4 h-4" />
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          <button
            onClick={sendMessage}
            disabled={!inputMessage.trim() || isLoading || isListening}
            className={`px-4 sm:px-4 rounded-xl flex items-center justify-center min-w-[44px] min-h-[44px] ${
              inputMessage.trim() && !isLoading && !isListening
                ? 'bg-gradient-to-r from-primary to-primary text-white hover:opacity-90'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            } transition-all`}
          >
            <Send className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
        
        {/* Voice input instructions */}
        {isListening && (
          <div className="mt-2 p-2 sm:p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs sm:text-sm text-red-700">
              <strong>Speaking:</strong> <span className="hidden sm:inline">Speak clearly. When you stop, the message will be sent automatically. Click the mic button or press Ctrl+Space to stop manually.</span>
              <span className="sm:hidden">Speak clearly. Message will auto-send when you stop.</span>
            </p>
          </div>
        )}
        
        <p className="text-xs text-gray-500 mt-2 text-center leading-relaxed">
          Powered by Tunisia Travel AI<span className="hidden sm:inline"> • Voice input auto-sends when you stop speaking</span>
        </p>
      </div>
    );
  };

  const renderMessage = (message: Message) => {
    const isBot = message.sender === 'bot';
    const { cleanText, circuits, hasTable } = processMarkdownResponse(message.text);
    
    return (
      <div
        key={message.id}
        className={`flex ${isBot ? 'justify-start' : 'justify-end'} mb-3 sm:mb-4`}
      >
        <div
          className={`max-w-[95%] sm:max-w-[85%] rounded-2xl px-3 sm:px-4 py-2 sm:py-3 ${
            isBot
              ? 'bg-gradient-to-r from-blue-50 to-indigo-50 rounded-tl-none'
              : 'bg-gradient-to-r from-primary to-primary text-white rounded-tr-none'
          }`}
        >
          {/* User message indicator for voice input */}
          {!isBot && message.text && (
            <div className="flex items-center gap-1 sm:gap-2 mb-2">
              <div className="flex items-center gap-1">
                <Mic className="w-2 h-2 sm:w-3 sm:h-3 text-white/80" />
                <span className="text-xs text-white/80">Voice message</span>
              </div>
            </div>
          )}

          {/* Message text */}
          <div className={`${isBot ? 'text-gray-800' : 'text-white'}`}>
            {isBot ? (
              <div className="prose prose-sm max-w-none">
                <ReactMarkdown
                  components={{
                    h1: ({node, ...props}) => <h1 className="text-xl font-bold mb-2" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-lg font-bold mb-2 mt-3" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-md font-bold mb-2 mt-3" {...props} />,
                    p: ({node, ...props}) => <p className="mb-2 leading-relaxed" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc pl-4 mb-2" {...props} />,
                    li: ({node, ...props}) => <li className="mb-1" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-bold" {...props} />,
                    em: ({node, ...props}) => <em className="italic" {...props} />,
                    br:({node, ...props}) => <br {...props} />,
                  }}
                >
                  {cleanText || message.text}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="leading-relaxed">{message.text}</div>
            )}
          </div>

          {/* Timestamp */}
          <div className={`text-xs mt-2 ${isBot ? 'text-gray-500' : 'text-white/80'}`}>
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>

          {/* Tourist Circuits Display */}
          {isBot && circuits && circuits.length > 0 && (
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <div className="w-2 h-4 sm:h-6 bg-purple-500 rounded-full"></div>
                <div className="flex items-center gap-2">
                  <Map className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                  <h3 className="font-bold text-base sm:text-lg text-gray-800">
                    Suggested Circuits ({circuits.length})
                  </h3>
                </div>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                {circuits.map(renderCircuitCard)}
              </div>
            </div>
          )}

          {/* Hotel Data Display */}
          {isBot && message.data?.hotels && message.context?.last_intent === 'hotel' && (
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <div className="w-2 h-4 sm:h-6 bg-primary rounded-full"></div>
                <h3 className="font-bold text-base sm:text-lg text-gray-800">
                  Found {message.data.hotels.length} Hotels
                </h3>
              </div>
              
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                {message.data.hotels.map((hotel) => (
                  <div key={hotel._id} className="transform transition-transform hover:scale-[1.01] sm:hover:scale-[1.02]">
                    <HotelCard item={transformHotelData(hotel)} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Restaurant Data Display */}
          {isBot && message.data?.restaurants && message.context?.last_intent === 'restaurant' && (
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <div className="w-2 h-4 sm:h-6 bg-orange-500 rounded-full"></div>
                <div className="flex items-center gap-2">
                  <Utensils className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                  <h3 className="font-bold text-base sm:text-lg text-gray-800">
                    Found {message.data.restaurants.length} Restaurants
                  </h3>
                </div>
              </div>
              
              <div className="space-y-4 sm:space-y-6">
                {message.data.restaurants.map((restaurant, index) => (
                  <div key={restaurant._id} className="transform transition-transform hover:scale-[1.01] sm:hover:scale-[1.02]">
                    <RestaurantCard 
                      item={transformRestaurantData(restaurant)}
                      isActive={false}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Handle both hotels and restaurants in same response */}
          {isBot && message.data?.hotels && message.data?.restaurants && (
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
              {/* Hotels Section */}
              {message.data.hotels.length > 0 && (
                <>
                  <div className="flex items-center gap-2 mb-3 sm:mb-4">
                    <div className="w-2 h-4 sm:h-6 bg-primary rounded-full"></div>
                    <h3 className="font-bold text-base sm:text-lg text-gray-800">
                      Found {message.data.hotels.length} Hotels
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4 sm:gap-6 mb-6 sm:mb-8">
                    {message.data.hotels.map((hotel) => (
                      <div key={hotel._id} className="transform transition-transform hover:scale-[1.01] sm:hover:scale-[1.02]">
                        <HotelCard item={transformHotelData(hotel)} />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Restaurants Section */}
              {message.data.restaurants.length > 0 && (
                <>
                  <div className="flex items-center gap-2 mb-3 sm:mb-4">
                    <div className="w-2 h-4 sm:h-6 bg-orange-500 rounded-full"></div>
                    <div className="flex items-center gap-2">
                      <Utensils className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                      <h3 className="font-bold text-base sm:text-lg text-gray-800">
                        Found {message.data.restaurants.length} Restaurants
                      </h3>
                    </div>
                  </div>
                  
                  <div className="space-y-4 sm:space-y-6">
                    {message.data.restaurants.map((restaurant) => (
                      <div key={restaurant._id} className="transform transition-transform hover:scale-[1.01] sm:hover:scale-[1.02]">
                        <RestaurantCard 
                          item={transformRestaurantData(restaurant)}
                          isActive={false}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <style jsx global>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-3px);
          }
        }

        @keyframes gentle-pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @keyframes sparkle {
          0%, 100% {
            transform: scale(0) rotate(0deg);
            opacity: 0;
          }
          50% {
            transform: scale(1) rotate(180deg);
            opacity: 1;
          }
        }

        @keyframes blink {
          0%, 90%, 100% {
            height: 4px;
          }
          95% {
            height: 1px;
          }
        }

        @keyframes notification-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.8;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        @keyframes voice-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }

        @keyframes voice-wave {
          0%, 100% {
            transform: scaleY(0.5);
            opacity: 0.5;
          }
          50% {
            transform: scaleY(1.5);
            opacity: 1;
          }
        }

        .animate-float {
          animation: float 4s ease-in-out infinite;
        }

        .animate-gentle-pulse {
          animation: gentle-pulse 1s ease-in-out infinite;
        }

        .animate-notification-pulse {
          animation: notification-pulse 2s infinite;
        }

        .animate-blink {
          animation: blink 4s infinite;
        }

        .animate-blink-delayed {
          animation: blink 4s infinite;
          animation-delay: 2s;
        }

        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }

        .animate-voice-pulse {
          animation: voice-pulse 1s ease-in-out infinite;
        }

        .voice-wave {
          animation: voice-wave 0.8s ease-in-out infinite;
        }

        .voice-wave:nth-child(2) {
          animation-delay: 0.2s;
        }

        .voice-wave:nth-child(3) {
          animation-delay: 0.4s;
        }

        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }

        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
      `}</style>

      {/* Chat Interface */}
      {isChatOpen && (
        <div className="fixed inset-x-2 bottom-20 sm:bottom-24 sm:right-5 sm:left-auto sm:w-96 z-50 animate-slide-in shadow-2xl bg-gradient-to-br from-primary via-primary to-primary-dark flex items-center justify-center shadow-primary/50 hover:shadow-xl hover:shadow-primary/70 transition-all duration-300 rounded-xl">
          <div className="w-full sm:w-96 h-[85vh] sm:h-[550px] max-h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
            {/* Chat Header with voice status */}
            <div className="bg-primary text-white p-3 sm:p-4 flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="relative">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Bot className="w-4 h-4 sm:w-6 sm:h-6" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full border-2 border-white"></div>
                </div>
                <div>
                  <h3 className="font-bold text-sm sm:text-md">Cathago Assistant</h3>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm text-white/80">Online</span>
                    {isVoiceEnabled && (
                      <span className="text-xs bg-green-500 text-white px-1.5 sm:px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Mic className="w-2 h-2 sm:w-3 sm:h-3" />
                        <span className="hidden sm:inline">Voice: ON</span>
                        <span className="sm:hidden">Voice</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={handleCloseChat}
                className="p-2 sm:p-2 hover:bg-white/20 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>

            {/* Messages Container */}
            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-2 sm:p-4 bg-gray-50"
            >
              {/* Welcome message about voice input */}
              {messages.length === 0 && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl border border-blue-100">
                  <div className="flex items-start gap-2 sm:gap-3 mb-2">
                    <div className="p-1.5 sm:p-2 bg-primary/10 rounded-full flex-shrink-0">
                      <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-sm sm:text-base text-gray-800">Voice Input Available!</h4>
                      <p className="text-xs sm:text-sm text-gray-600">
                        Click the microphone button to speak. When you stop, the message auto-sends.
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>• Speak clearly into your microphone</p>
                    <p>• Available in multiple languages</p>
                    <p>• Message auto-sends when you stop speaking</p>
                  </div>
                </div>
              )}
              
              {messages.map(renderMessage)}
              
              {isLoading && (
                <div className="flex justify-start mb-4">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl rounded-tl-none px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                        <div className="w-2 h-2 bg-primary rounded-full animate-pulse delay-150"></div>
                        <div className="w-2 h-2 bg-primary rounded-full animate-pulse delay-300"></div>
                      </div>
                      <span className="text-gray-600">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input Area */}
            {renderInputArea()}
          </div>
        </div>
      )}

      {/* Floating Chat Button */}
      <div
        ref={containerRef}
        className={`fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-50 cursor-pointer transition-all duration-300 hover:scale-105 ${currentSize.container} ${className}`}
        onMouseEnter={() => {setShowNotification(true);setIsHovered(true)}}
        onMouseLeave={() => {setShowNotification(false);setIsHovered(false)}}
        onClick={handleClick}
        role="button"
        aria-label="Chat assistant"
        tabIndex={0}
      >
        {/* Main chatbot container */}
        <div className={`
          relative w-full h-full
          ${currentSize.borderRadius}
          bg-primary
          flex items-center justify-center
          shadow-lg shadow-primary/50
          hover:shadow-xl hover:shadow-primary/50
          transition-all duration-300
          animate-float
          ${isHovered ? 'animate-gentle-pulse' : ''}
          group
          backdrop-blur-sm
        `}>
          
          {/* Bot icon */}
          <div className="relative">
            {isActive ? (
              <MessageCircle className={`${currentSize.icon} text-white transition-transform duration-300 group-hover:rotate-12`} />
            ) : (
              <Bot className={`${currentSize.icon} text-white transition-transform duration-300 group-hover:rotate-12`} />
            )}
            
            {/* Animated eyes */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex gap-2">
              <div 
                className={`${currentSize.eye} bg-white rounded-full animate-blink`}
                style={{
                  transform: `translate(${eyePosition.x}px, ${eyePosition.y}px)`
                }}
              />
              <div 
                className={`${currentSize.eye} bg-white rounded-full animate-blink-delayed`}
                style={{
                  transform: `translate(${eyePosition.x}px, ${eyePosition.y}px)`
                }}
              />
            </div>
          </div>

          {/* Sparkle effects */}
          {showSparkles && (
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full"
                  style={{
                    left: `${50 + 30 * Math.cos((i * Math.PI) / 2)}%`,
                    top: `${50 + 30 * Math.sin((i * Math.PI) / 2)}%`,
                    animation: `sparkle 0.8s ease-in-out ${i * 0.2}s`
                  }}
                />
              ))}
            </div>
          )}

          {/* Notification dot */}
          {hasNotification && showNotification && !isChatOpen && (
            <div className={`
              absolute ${currentSize.notification}
              bg-red-500 rounded-full border-2 border-white
              animate-notification-pulse
            `} />
          )}
        </div>

        {/* Tooltip - Appears on hover */}
        <div
          className={`
            absolute -top-12 -left-1/2 -translate-x-1/2
            ${currentSize.tooltip}
            bg-primary text-white
            rounded-lg shadow-lg
            whitespace-nowrap
            pointer-events-none
            transition-all duration-300
            after:content-[''] after:absolute after:top-full after:left-5/6
            after:-translate-x-1/2
            after:border-4 after:border-transparent
            after:border-t-primary
            ${isHovered && !isChatOpen ? 'opacity-100 visible translate-y-0 animate-fade-in' : 'opacity-0 invisible translate-y-2'}
          `}
        >
          Need help? Click to chat!
        </div>

        {/* Click effect ring */}
        <div className={`
          absolute inset-0
          ${currentSize.borderRadius}
          border-2 border-primary
          scale-0 group-active:scale-110
          opacity-0 group-active:opacity-100
          transition-all duration-200
          pointer-events-none
        `} />
      </div>
    </>
  );
};

export default AnimatedChatbot;